import React, { ComponentProps, MouseEventHandler, useContext, useEffect, useState } from 'react';
import { noop } from 'lodash';
import { LinkCard, Container } from '@fiverr-private/layout_components';
import { getContext } from '@fiverr-private/fiverr_context';
import { useImpressionItem } from '@fiverr-private/impressionable';
import { CommonBiEnrichment, IImpressionData } from '@fiverr-private/homepage_common_types';
import {
    sendClickEvent,
    IClickData,
    getMixPanelCommonProperties,
    getBuyerState,
    onImpression,
} from '@fiverr-private/homepage_utils';
import { DrawerBaseProps } from '@fiverr-private/jtbd_sdk';
import { Button } from '@fiverr-private/button';
import { mixpanel } from '@fiverr-private/obs';
import { theme } from '@fiverr-private/theme';
import { PrimaryProjectManagementSDKComponentImpl } from '@fiverr-private/primary_project_management';
import { JobTbdPage } from '../JobTbdPage/JobTbdPage';
import { DrawerContext } from '../../context/drawer/drawer.context';
import { ThemeContext } from '../../context/theme/theme.context';
import { MetadataContext } from '../../context/metadata/metadata.context';
import { enrichURI } from '../../../utils/links';
import { useUser } from '../../context/user/userProvider';
import { JobsTbdModalMapper } from '../../../lib/utils/jobsTBDMapper';
import { JobTbdCardProps } from './JobTbdCard.types';
import { useEmphasizeCard } from './JobTbdCard.hooks';
import { cardBasicStyle, cardStyle, outlinedWhiteButtonStyle } from './JobTbdCard.ve.css';
import { getCardStaticStyles, openDrawerForPage, sendGrafanaMetric } from './JobTbdCard.utils';
import { getAdditionalActionProps } from './utils';

// This is a reminder for us to export the modal to a package and lazy load it
// const ProjectManagementModalImport = () =>
//     import(/* webpackChunkName: 'ProjectManagementModal' */ '@fiverr-private/primary_project_management').then(
//         (module) => ({
//             default: module.PrimaryProjectManagementSDKComponentImpl.ProjectManagementModal,
//         })
//     );

const COMPONENT_NAME = 'job_to_be_done';

export const JobTbdCard = (props: JobTbdCardProps): JSX.Element => {
    const { jobType, jobData, jobsCount, onDismiss, withCta } = props;

    const {
        headerText,
        jobPages,
        clickAction,
        onCardClick,
        onCardImpression,
        customActionCallback,
        isEmphasizedCard = false,
        dismissInfo,
    } = jobData;

    const { pageCtxId, isTouch } = getContext();
    const [currentPageIndex, setCurrentPageIndex] = useState(0);
    const [cardHasBeenClicked, setCardHasBeenClicked] = useState(false);
    const [isCtaFocused, setIsCtaFocused] = useState(false);

    const shouldDisplayEmphasize = useEmphasizeCard(isEmphasizedCard, cardHasBeenClicked);
    const { buyerInsights, userData } = useUser();
    const { layoutId } = useContext(ThemeContext);
    const drawerContext = useContext(DrawerContext);
    const metadataContext = useContext(MetadataContext);

    const [isProjectManagementModalOpen, setIsProjectManagementModalOpen] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [impressionId, setImpressionId] = useState<string>('');

    const ModalComponent = JobsTbdModalMapper[jobType]?.ModalComponent;

    useEffect(() => {
        // every time we switch to a new page, reset to state of card to not being clicked
        setCardHasBeenClicked(false);
    }, [currentPageIndex]);

    const currentPage = jobPages[currentPageIndex];
    const biEnrichment = currentPage.biEnrichment;

    const buyerSegment = buyerInsights?.segment;
    const buyerState = getBuyerState(buyerSegment);
    const impressionEnrichment: CommonBiEnrichment = {
        componentName: COMPONENT_NAME,
        componentType: jobType,
        layoutId,
        buyerState,
        order: biEnrichment?.order,
        inboxMessage: biEnrichment?.inboxMessage,
    };

    const [ref, itemId] = useImpressionItem({
        enrichment: impressionEnrichment,
        sendImpressionToBQ: false,
        onImpression: async (impressionData: IImpressionData) => {
            setImpressionId(impressionData.imp_id);
            await onImpression(impressionData);

            onCardImpression?.({ biEnrichment });
            mixpanel.track('Home - JTBD Impression', {
                'JTBD Name': jobType,
                ...biEnrichment,
                ...getMixPanelCommonProperties({
                    layoutId: impressionData.layoutId,
                    buyerState: impressionData.buyerState,
                }),
            });
        },
    });

    const onClick: MouseEventHandler = () => {
        onCardClick?.({ biEnrichment });
        const clickData: IClickData = {
            imp: {
                id: itemId,
            },
        };

        sendClickEvent(clickData);
        sendGrafanaMetric(jobType, 'click', buyerSegment);
        mixpanel.track('Home - JTBD Click', {
            'JTBD Name': jobType,
            ...biEnrichment,
            ...getMixPanelCommonProperties({
                layoutId,
                buyerState,
            }),
        });

        switch (clickAction.type) {
            case 'link':
                const enrichedUrl = enrichURI(clickAction.href, { source: 'tailored_homepage', ref_ctx_id: pageCtxId });

                window.open(enrichedUrl, '_self');
                break;
            case 'drawer':
                const advancePage = () => {
                    if (currentPageIndex < jobPages.length - 1) {
                        setCurrentPageIndex((prev) => prev + 1);
                    }
                };

                const drawerBaseProps: DrawerBaseProps = {
                    userData,
                };

                openDrawerForPage(drawerContext, jobType, biEnrichment, advancePage, drawerBaseProps);
                break;
            case 'modal':
                customActionCallback?.({
                    ...metadataContext,
                    hanlders: {
                        openModal: () => {
                            setIsModalOpen(true);
                        },
                    },
                });

                break;
            case 'custom': // TODO: refactor the PM modal to use the ModalComponent
                customActionCallback?.({
                    ...metadataContext,
                    hanlders: {
                        openModal: () => {
                            setIsProjectManagementModalOpen(true);
                        },
                    },
                });
                break;
            default:
                const _exhaustiveCheck: never = clickAction;

                break;
        }
        setCardHasBeenClicked(true);
    };

    const handleReportButtonClick = (args?: IClickData) => {
        const commonClickData: IClickData = {
            imp: {
                id: impressionId,
            },
        };
        const enrichedClickData = {
            ...commonClickData,
            page_element_name: `JTBD_card_${jobType}`,
            ...args,
        };

        sendClickEvent(enrichedClickData);
        mixpanel.track(`Home - JTBD - Click`, {
            componentType: jobType,
            ...getMixPanelCommonProperties({ layoutId, buyerState, componentName: COMPONENT_NAME }),
        });
    };

    const calcShouldShowCta = () => {
        if (isTouch || !currentPage.ctaText || !withCta) {
            return false;
        }

        return jobsCount < 3;
    };

    const shouldShowCta = calcShouldShowCta();
    const ctaButton = (
        <Container display={{ default: 'none', sm: 'block' }} paddingLeft="4">
            <Button
                variant={isCtaFocused ? 'filled' : 'outline'}
                intent="primary"
                data-testid="jtbd-cta"
                onClick={onClick}
                className={outlinedWhiteButtonStyle}
                {...(shouldShowCta
                    ? {}
                    : (getAdditionalActionProps(clickAction, pageCtxId) as Partial<ComponentProps<typeof Button>>))}
                role={clickAction.type === 'link' ? 'link' : undefined}
                onFocus={() => setIsCtaFocused(true)}
                onBlur={() => setIsCtaFocused(false)}
            >
                {currentPage.ctaText}
            </Button>
        </Container>
    );

    return (
        <>
            <LinkCard
                ref={ref}
                height={{ default: '106px', sm: '112px' }}
                className={`${shouldShowCta ? cardBasicStyle : cardStyle}`}
                {...getCardStaticStyles('100%')}
                onClick={shouldShowCta ? noop : onClick}
                display="block"
                background="white"
                borderColor={{ default: theme.colors.transparent, focus: shouldShowCta ? undefined : 'grey_1200' }}
                outlineWidth={{ default: 'sm' }}
                role={clickAction.type === 'link' && !shouldShowCta ? 'link' : undefined}
                cursor={shouldShowCta ? 'auto' : 'pointer'}
                tabIndex={0}
                marginTop="1"
                borderRadius="xl"
                aria-label="Job to be done card"
                data-testid="jtbd-card"
                {...(shouldShowCta
                    ? {}
                    : (getAdditionalActionProps(clickAction, pageCtxId) as Partial<ComponentProps<typeof LinkCard>>))}
            >
                <JobTbdPage
                    headerText={headerText}
                    isEmphasized={shouldDisplayEmphasize}
                    cta={shouldShowCta ? ctaButton : undefined}
                    dismissInfo={{
                        isDismissible: dismissInfo.isDismissible,
                        onDismiss: (e) => onDismiss(e, jobType, dismissInfo, itemId),
                    }}
                    {...currentPage}
                />
            </LinkCard>
            {/* TODO: refactor the PM modal to use the ModalComponent */}
            <PrimaryProjectManagementSDKComponentImpl.ProjectManagementModal
                isOpen={isProjectManagementModalOpen}
                onClose={() => setIsProjectManagementModalOpen(false)}
                reportClick={handleReportButtonClick}
            />
            {ModalComponent && <ModalComponent isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />}
        </>
    );
};
